import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLiveBackground } from "../liveBackground/LiveBackground";
import "./liveAudio.css";

const AudioContext = createContext(null);
const STORAGE_KEY = "live-full-audio";
const AUDIO_BASE_URL = "https://vnonemedia.s3.ap-southeast-1.amazonaws.com/sounds";
const AUDIO_TYPE_ALIASES = { tc_minhngoc: "tc-minhngoc" };
export const useLiveAudio = () => useContext(AudioContext);

export function getLiveAudioUrl(lotteryType) {
    const type = String(lotteryType || "").toLowerCase();
    const folder = AUDIO_TYPE_ALIASES[type] || type;
    return folder ? `${AUDIO_BASE_URL}/${folder}/sound-live.mp3` : "";
}

function readPreferences() {
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return {
            muted: saved?.muted === true,
            volume: typeof saved?.volume === "number" && Number.isFinite(saved.volume)
                ? Math.max(0, Math.min(1, saved.volume)) : 0.3,
        };
    } catch {
        return { muted: false, volume: 0.3 };
    }
}

export default function LiveAudio({ children }) {
    const lotteryType = useLiveBackground();
    const audioRef = useRef(null);
    const mounted = useRef(false);
    const [preferences, setPreferences] = useState(readPreferences);
    const [playing, setPlaying] = useState(false);
    const [error, setError] = useState("");
    const preferencesRef = useRef(preferences);
    preferencesRef.current = preferences;
    const playingRef = useRef(false);
    const startingRef = useRef(false);

    useEffect(() => {
        mounted.current = true;
        const audio = audioRef.current;
        return () => {
            mounted.current = false;
            audio.pause();
        };
    }, []);

    useEffect(() => {
        audioRef.current.volume = preferences.volume;
        audioRef.current.muted = preferences.muted;
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences)); } catch {}
    }, [preferences]);

    // Attempt autoplay on live entry, or retry directly from a user gesture.
    const start = useCallback(async () => {
        const audio = audioRef.current;
        if (!audio || playingRef.current || startingRef.current) return;
        audio.volume = preferencesRef.current.volume;
        audio.muted = preferencesRef.current.muted;
        setError("");
        startingRef.current = true;
        try {
            // Calling play() directly lets the browser begin loading the remote source
            // while preserving the user's gesture for autoplay policy checks.
            await audio.play();
        } catch (reason) {
            if (!mounted.current) return;
            setPlaying(false);
            setError(reason?.name === "NotAllowedError"
                ? "Click Enable sound to allow playback."
                : "Audio could not play. Check the audio file and try again.");
        } finally {
            startingRef.current = false;
        }
    }, []);

    const toggleSound = () => {
        if (playing && !preferences.muted && preferences.volume > 0) {
            audioRef.current.muted = true;
            setPreferences(previous => ({ ...previous, muted: true }));
        } else {
            const volume = preferences.volume || 0.3;
            audioRef.current.muted = false;
            audioRef.current.volume = volume;
            preferencesRef.current = { muted: false, volume };
            setPreferences({ muted: false, volume });
            void start();
        }
    };

    return <AudioContext.Provider value={{ start, toggleSound, playing, preferences, setPreferences, error }}>
        <audio ref={audioRef}
            src={getLiveAudioUrl(lotteryType) || undefined}
            loop preload="none"
            onPlaying={() => { playingRef.current = true; setPlaying(true); }}
            onPause={() => { playingRef.current = false; setPlaying(false); }}
            onError={() => {
                playingRef.current = false;
                setPlaying(false);
                setError("Audio could not load. Check the audio file and try again.");
            }} />
        {children}
    </AudioContext.Provider>;
}


export function LiveAudioControls() {
    const audio = useLiveAudio();
    if (!audio) return null;
    const { toggleSound, playing, preferences, setPreferences, error } = audio;
    const audible = playing && !preferences.muted && preferences.volume > 0;
    return (
        <div className="live-audio-controls d-none" aria-label="Background sound controls">
            <button type="button" onClick={toggleSound}
                aria-label={audible ? "Mute" : "Enable sound"}
                title={audible ? "Mute" : "Enable sound"}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                    strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <path d="M11 5 6 9H3v6h3l5 4Z" />
                    {audible ? <>
                        <path d="M15 8a6 6 0 0 1 0 8" />
                        <path d="M18 5a10 10 0 0 1 0 14" />
                    </> : <path d="m16 9 6 6m0-6-6 6" />}
                </svg>
            </button>
            <input type="range" aria-label="Background sound volume" min="0" max="1" step="0.05"
                value={preferences.volume}
                onChange={event => setPreferences(previous => ({ ...previous, volume: Number(event.target.value) }))} />
            {error && <span role="status">{error}</span>}
        </div>
    );
}


// Mounted only while results are visible; no controls are rendered on the broadcast.
export function LiveAudioAutostart() {
    const audio = useLiveAudio();
    const start = audio?.start;
    const playing = audio?.playing;
    const muted = audio?.preferences.muted;
    const volume = audio?.preferences.volume;

    useEffect(() => {
        if (start) void start();
    }, [start]);

    useEffect(() => {
        if (!start || playing || muted || volume === 0) return;
        const unlock = (event) => {
            if (event.type === "keydown" && (event.repeat || event.ctrlKey || event.metaKey || event.altKey)) return;
            void start();
        };
        document.addEventListener("click", unlock);
        document.addEventListener("keydown", unlock);
        return () => {
            document.removeEventListener("click", unlock);
            document.removeEventListener("keydown", unlock);
        };
    }, [start, playing, muted, volume]);
    return null;
}
